import React, { useState, useCallback } from 'react';
import type { Task, TaskPriority, TaskStatus } from '../types/project';
import type { Person } from '../types/people';
import { AssigneesDisplay } from './AssigneeDropdown';
import TaskAssigneesPicker from './TaskAssigneesPicker';
import { formatDateDDMMYYYY } from '../utils/date';
import { TASK_STATUSES, STATUS_LABEL, STATUS_BADGE } from '../utils/taskStatus';
import { projectLabel } from '../utils/task';

const COLS: { status: TaskStatus; label: string }[] = TASK_STATUSES.map((status) => ({
  status,
  label: STATUS_LABEL[status],
}));

const PRIORITY_COLOR: Record<string, string> = {
  LOW: 'var(--color-text-muted)',
  MEDIUM: 'var(--color-blue-dark)',
  HIGH: 'var(--color-coral)',
};

const DRAG_DATA_KEY = 'application/x-jambo-task-id';

interface KanbanBoardProps {
  tasks: Task[];
  people?: Person[];
  onTaskClick: (task: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onPriorityChange?: (taskId: string, priority: TaskPriority) => void;
  onAssigneesChange?: (taskId: string, assigneeIds: string[]) => void;
  onAddTask?: (status: TaskStatus) => void;
  showAddCard?: boolean;
}

export default function KanbanBoard({
  tasks,
  people = [],
  onTaskClick,
  onStatusChange,
  onPriorityChange,
  onAssigneesChange,
  onAddTask,
  showAddCard = true,
}: KanbanBoardProps) {
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const byStatus = COLS.reduce<Record<TaskStatus, Task[]>>(
    (acc, { status }) => {
      acc[status] = tasks.filter((t) => t.status === status && !t.parent_task_id);
      return acc;
    },
    { TO_DO: [], DOING: [], COMPLETED: [], BLOCKED: [], CANCELLED: [] } as Record<TaskStatus, Task[]>
  );

  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData(DRAG_DATA_KEY, taskId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, status: TaskStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, newStatus: TaskStatus) => {
      e.preventDefault();
      setDragOverColumn(null);
      setDraggingId(null);
      const taskId = e.dataTransfer.getData(DRAG_DATA_KEY);
      if (!taskId) return;
      const task = tasks.find((t) => t.id === taskId);
      if (task && task.status !== newStatus) {
        onStatusChange(taskId, newStatus);
      }
    },
    [tasks, onStatusChange]
  );

  return (
    <div style={{ display: 'flex', gap: '24px', paddingBottom: '16px', minWidth: 0 }}>
      {COLS.map(({ status, label }) => (
        <div
          key={status}
          style={{
            flex: 1,
            minWidth: 0,
            background: dragOverColumn === status ? 'rgba(30, 58, 95, 0.08)' : STATUS_BADGE[status].bg,
            border: `1px solid ${dragOverColumn === status ? 'var(--color-blue-dark)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius)',
            padding: '16px',
            transition: 'background 150ms, border-color 150ms',
          }}
          onDragOver={(e) => handleDragOver(e, status)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, status)}
        >
          <div
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: STATUS_BADGE[status].color,
              marginBottom: '12px',
            }}
          >
            {label}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '80px' }}>
            {byStatus[status].map((task) => (
              <div
                key={task.id}
                draggable
                onDragStart={(e) => handleDragStart(e, task.id)}
                onDragEnd={handleDragEnd}
                role="button"
                tabIndex={0}
                onClick={() => onTaskClick(task)}
                onKeyDown={(e) => e.key === 'Enter' && onTaskClick(task)}
                style={{
                  padding: '16px',
                  background: draggingId === task.id ? 'var(--color-border)' : 'white',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  cursor: 'grab',
                  opacity: draggingId === task.id ? 0.7 : 1,
                  transition: 'box-shadow 200ms',
                }}
                onMouseOver={(e) => {
                  if (draggingId !== task.id) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>{task.title}</div>
                {task.theme_name && (
                  <div
                    title={projectLabel(task)}
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-text-muted)',
                      marginBottom: '6px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {projectLabel(task)}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: 'var(--color-text-muted)', gap: '8px' }}>
                  <span>
                    {task.due_date ? (
                      <span style={{ color: new Date(task.due_date) < new Date() ? 'var(--color-coral)' : undefined }}>
                        {formatDateDDMMYYYY(task.due_date)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </span>
                  <span>
                    {people.length > 0 ? (
                      onAssigneesChange ? (
                        <TaskAssigneesPicker
                          task={task}
                          people={people}
                          onChange={(ids) => onAssigneesChange(task.id, ids)}
                        />
                      ) : (
                        <AssigneesDisplay
                          people={people}
                          assigneeIds={
                            task.assignee_ids && task.assignee_ids.length > 0
                              ? task.assignee_ids
                              : task.assignee
                              ? [task.assignee]
                              : []
                          }
                        />
                      )
                    ) : (
                      task.assignee ?? '—'
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <select
                    value={task.status}
                    onChange={(e) => {
                      e.stopPropagation();
                      onStatusChange(task.id, e.target.value as TaskStatus);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontSize: '12px',
                      padding: '2px 6px',
                      borderRadius: 'var(--radius)',
                      border: 'none',
                      background: STATUS_BADGE[task.status].bg,
                      color: STATUS_BADGE[task.status].color,
                      cursor: 'pointer',
                      fontWeight: 600,
                      appearance: 'menulist',
                    }}
                  >
                    {TASK_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                  <select
                    value={task.priority ?? 'MEDIUM'}
                    onChange={(e) => {
                      e.stopPropagation();
                      onPriorityChange?.(task.id, e.target.value as TaskPriority);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontSize: '12px',
                      padding: '2px 6px',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--color-border)',
                      background: 'white',
                      color: PRIORITY_COLOR[task.priority] ?? 'var(--color-text-muted)',
                      cursor: 'pointer',
                      maxWidth: '88px',
                      appearance: 'menulist',
                    }}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>
            ))}
            {showAddCard && onAddTask && (
              <button
                type="button"
                onClick={() => onAddTask(status)}
                style={{
                  padding: '12px',
                  border: '1px dashed var(--color-border)',
                  borderRadius: 'var(--radius)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                + Add task
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
